import React, { useState, useEffect, useRef } from 'react';
import { useAdminAvatar } from '../../lib/avatars';
import { useTranslation } from 'react-i18next';
import { Icon } from '../icons';
import { Avatar } from '../ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useIsMobile } from '../../lib/useBreakpoint';
import { useAuth } from '../../contexts/AuthContext';
import { v2Notifications, showBrowserNotification, type AdminNotification } from '../../lib/notifications';
import { toast } from '../../lib/toast';
import { GlobalSearch } from './GlobalSearch';
import { formatDayMonth } from '../../lib/dateFormat';

/** Initials helper: "Anna Schmidt" → "AS", "anna@x.de" → "AN", fallback "??". */
function initialsFor(name: string | undefined, email: string | undefined): string {
  const src = (name && name.trim()) || (email ? email.split('@')[0] : '');
  if (!src) return '??';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

/** "Anna Schmidt" → "Anna S.", "anna@x.de" → "anna" — for the compact pill. */
function shortLabel(name: string | undefined, email: string | undefined): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0]} ${parts[1][0]}.`;
    return parts[0];
  }
  if (email) return email.split('@')[0];
  return '—';
}

interface HeaderProps {
  pageTitle: string;
  subtitle?: string;
  dark: boolean;
  setDark: (v: boolean | ((p: boolean) => boolean)) => void;
  setPage: (page: string) => void;
  onLogout: () => void;
  /** Called when the user taps the hamburger button (mobile only). */
  onOpenMobileMenu?: () => void;
  /** Optional context node left of the page title (e.g. a „back" button). */
  leadingSlot?: React.ReactNode;
  /** Optional context node right of the page title (e.g. a breadcrumb). */
  trailingSlot?: React.ReactNode;
}

/** Relative Zeitangabe ("vor 5 Min." / "vor 2 Std." / Datum). */
function relTime(iso: string | null, tr: (k: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return tr('header.notifications.just_now');
  if (mins < 60) return tr('header.notifications.ago', { time: tr('header.notifications.minutes_short', { count: mins }) });
  const hours = Math.round(mins / 60);
  if (hours < 24) return tr('header.notifications.ago', { time: tr('header.notifications.hours_short', { count: hours }) });
  return formatDayMonth(d, '');
}

export function Header({ pageTitle, subtitle, dark, setDark, setPage, onLogout, onOpenMobileMenu, leadingSlot, trailingSlot }: HeaderProps) {
  const t = getThemeColors(dark);
  const { t: i18nT } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const initials = initialsFor(user?.name, user?.email);
  const ownAvatar = useAdminAvatar(Number(user?.id) || null);
  const [ownAvatarFailed, setOwnAvatarFailed] = useState(false);
  useEffect(() => { setOwnAvatarFailed(false); }, [ownAvatar]);
  const label    = shortLabel(user?.name, user?.email);
  const fullName = user?.name ?? user?.email ?? '—';
  const email    = user?.email ?? '';
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  // WP4: echte Benachrichtigungen (admin_notifications) — 60s-Polling.
  // Neue ungelesene erscheinen als Toast + Browser-Notification; die Glocke
  // zeigt die letzten Einträge und markiert Ungelesene beim Öffnen als gelesen.
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const announcedRef = useRef<Set<number>>(new Set());
  const firstLoadRef = useRef(true);
  const unreadCount = notifications.filter(n => n.read_at == null).length;

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const list = await v2Notifications.list();
        if (!alive) return;
        setNotifications(list);
        // Nur nach dem Erstladen ankündigen — alte Ungelesene beim Login
        // erscheinen in der Glocke, aber nicht als Popup-Schwall.
        const fresh = list.filter(n => n.read_at == null && !announcedRef.current.has(n.id));
        fresh.forEach(n => announcedRef.current.add(n.id));
        if (!firstLoadRef.current) {
          fresh.slice(0, 5).forEach(n => {
            toast(n.message, 'info', n.subject);
            showBrowserNotification(n.subject, n.message);
          });
        }
        firstLoadRef.current = false;
      } catch { /* silent — nächster Tick */ }
    };
    void load();
    // 30s statt 60s: seit @-Erwähnungen im Chat hier ankommen, ist eine Minute
    // Verzögerung für eine direkte Ansprache zu lang.
    const timer = window.setInterval(() => void load(), 30000);
    return () => { alive = false; window.clearInterval(timer); };
  }, []);

  const openNotifications = () => {
    setNotifOpen(v => !v);
    setUserOpen(false);
    const unreadIds = notifications.filter(n => n.read_at == null).map(n => n.id);
    if (unreadIds.length > 0) {
      void v2Notifications.markRead(unreadIds).catch(() => { /* silent */ });
      const now = new Date().toISOString();
      setNotifications(prev => prev.map(n => n.read_at == null ? { ...n, read_at: now } : n));
    }
  };

  useEffect(() => {
    const onClick = () => {
      setNotifOpen(false);
      setUserOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <header style={{
      borderBottom: `1px solid ${t.border}`,
      background: t.card,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '0 14px' : '0 24px',
      height: 64, gap: isMobile ? 10 : 16, flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' }}>
        {isMobile && onOpenMobileMenu && (
          <button
            onClick={onOpenMobileMenu}
            aria-label={i18nT('header.action.open_mobile_menu')}
            style={{
              width: 38, height: 38, borderRadius: 9,
              border: `1px solid ${t.border}`, background: t.card,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <Icon.Menu c={t.muted}/>
          </button>
        )}
        {/* T5: optionaler Kontext-Slot links vom Seitentitel (z.B. „Zurück zur Liste") */}
        {leadingSlot}
        {/* Handy: neben Menü + Zurück-Button bleiben für den Titel nur ein
            paar Pixel („I…") — dann lieber ganz weglassen. */}
        {!(isMobile && leadingSlot) && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: t.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pageTitle}</div>
            {subtitle && !isMobile && <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>{subtitle}</div>}
          </div>
        )}
        {/* T5: optionaler Kontext-Slot rechts vom Seitentitel (z.B. Breadcrumb/ID) */}
        {trailingSlot}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexShrink: 0 }}>
        {/* Search — desktop only */}
        {!isMobile && <GlobalSearch dark={dark} />}

        {/* Theme toggle */}
        <button
          onClick={() => setDark((d: boolean) => !d)}
          title={i18nT('header.action.theme_toggle')}
          style={{
            width: 40, height: 40, borderRadius: 9999,
            border: `1px solid ${t.border}`, background: t.card,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          {dark ? <Icon.Sun c={t.muted}/> : <Icon.Moon c={t.muted}/>}
        </button>

        {/* Language switcher — right of theme toggle, left of notifications */}
        <LanguageSwitcher dark={dark} variant="header" compact={isMobile} />

        {/* Notifications */}
        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button
            aria-label={i18nT('header.notifications.title')}
            onClick={openNotifications}
            style={{
              width: 40, height: 40, borderRadius: 9999,
              border: `1px solid ${t.border}`, background: t.card,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', position: 'relative',
            }}
          >
            <Icon.Bell c={t.muted}/>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 8, right: 9,
                width: 8, height: 8, borderRadius: '50%',
                background: '#fb6514', border: `2px solid ${t.card}`,
              }}/>
            )}
          </button>
          {notifOpen && (
            <div className="fade-in" style={{
              // Handy: 320px ab der Glocke liefen links aus dem Bildschirm —
              // dort als fixe Fläche mit 14px Rand unter der Navbar.
              ...(isMobile
                ? { position: 'fixed' as const, left: 14, right: 14, top: 60 }
                : { position: 'absolute' as const, right: 0, top: 48, width: 320 }),
              background: t.card, border: `1px solid ${t.border}`,
              borderRadius: 14, boxShadow: '0 12px 24px -8px rgba(16,24,40,0.15)',
              padding: 12, zIndex: 999,
            }}>
              <div style={{
                fontWeight: 600, fontSize: 13, color: t.heading,
                marginBottom: 10, paddingBottom: 8,
                borderBottom: `1px solid ${t.borderSoft}`,
              }}>
                {i18nT('header.notifications.title')}
              </div>
              {notifications.length === 0 && (
                <div style={{ fontSize: 12, color: t.muted, padding: '10px 4px', fontStyle: 'italic' }}>
                  {i18nT('header.notifications.empty')}
                </div>
              )}
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {notifications.map((n, i) => (
                  <div key={n.id} style={{
                    display: 'flex', gap: 10, padding: '8px 4px',
                    borderBottom: i < notifications.length - 1 ? `1px solid ${t.borderSoft}` : 'none',
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: BRAND_LIGHT,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 600, color: BRAND, flexShrink: 0,
                    }}>
                      {(n.subject || '•')[0].toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: t.text, lineHeight: 1.4 }}>
                        <strong style={{ color: t.heading }}>{n.subject}</strong>
                        {n.message ? ` — ${n.message}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>{relTime(n.created_at, i18nT)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setUserOpen(v => !v); setNotifOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${t.border}`, background: t.card,
              cursor: 'pointer', padding: '4px 10px 4px 4px', borderRadius: 9999,
            }}
          >
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: BRAND,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden',
            }}>
              {/* Feedback 11.10.2026: eigenes Profilbild, sonst Initialen. */}
              {ownAvatar && !ownAvatarFailed
                ? <img src={ownAvatar} alt={initials} onError={() => setOwnAvatarFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
            </div>
            {!isMobile && <span style={{ fontSize: 12, fontWeight: 500, color: t.text }}>{label}</span>}
            {!isMobile && <Icon.Chevron c={t.muted}/>}
          </button>
          {userOpen && (
            <div className="fade-in" style={{
              position: 'absolute', right: 0, top: 48, width: 220,
              background: t.card, border: `1px solid ${t.border}`,
              borderRadius: 14, boxShadow: '0 12px 24px -8px rgba(16,24,40,0.15)',
              padding: 10, zIndex: 999,
            }}>
              <div style={{ padding: '6px 8px 10px', borderBottom: `1px solid ${t.borderSoft}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{fullName}</div>
                {email && <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>{email}</div>}
              </div>
              {[
                { label: i18nT('header.user_menu.my_profile'), action: () => setPage('profil') },
                { label: i18nT('header.user_menu.settings'), action: () => setPage('einstellungen') },
              ].map((item, i) => (
                <div
                  key={i}
                  onClick={() => { item.action(); setUserOpen(false); }}
                  style={{
                    fontSize: 13, color: t.text,
                    padding: '8px 10px', borderRadius: 7,
                    cursor: 'pointer', fontWeight: 500,
                    marginTop: i === 0 ? 6 : 2,
                  }}
                  onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = t.hover}
                  onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
                >
                  {item.label}
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${t.borderSoft}`, marginTop: 6, paddingTop: 6 }}>
                <div
                  onClick={() => { onLogout(); setUserOpen(false); }}
                  style={{
                    fontSize: 13, color: '#f04438',
                    padding: '8px 10px', borderRadius: 7,
                    cursor: 'pointer', fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = dark ? '#2a1515' : '#fef3f2'}
                  onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
                >
                  <Icon.Logout c="#f04438"/> {i18nT('header.user_menu.logout')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
